import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { StorageTypes } from '../../../../tests/mocks/utils';
import { SHADOW_PROJECT_TYPE, SOCIAL_LOGIN } from '../../../constants';
import { useCurrentProject } from '../../../contexts/settings';
import { usePaths } from '../../../routes/routesHelper';
import { RouteParams } from '../../../routes/types';
import { isPasskeyAvailable } from '../../../utils';
import { NavigationWrapper, TabItems } from '../../ui';

type Props = React.PropsWithChildren<RouteComponentProps>;

const AuthenticationTabs = ({ match, children }: Props): JSX.Element => {
  const {
    callbackUrlsSettingsPath,
    dashboardPath,
    enterpriseConnectionsPath,
    miscellaneousPath,
    navigationPath,
    passkeyPath,
    platformSettingsPath,
    socialConnectionPath
  } = usePaths();
  const { projectId } = match.params as RouteParams;
  const intl = useIntl();
  const { currentProject } = useCurrentProject(projectId);

  const isPasskeyShown = isPasskeyAvailable(currentProject);

  const tabItems: TabItems = useMemo(() => {
    const baseTabs = [];

    if (currentProject?.type !== SOCIAL_LOGIN) {
      baseTabs.push({
        label: intl.formatMessage({ id: 'social-connection.tabs.social-connection-path' }),
        href: socialConnectionPath(projectId)
      });
    }

    if (
      currentProject?.users_storage === StorageTypes.XSOLLA ||
      currentProject?.users_storage === StorageTypes.CUSTOM
    ) {
      baseTabs.push({
        label: intl.formatMessage({ id: 'settings.tabs.platform-settings' }),
        href: platformSettingsPath(projectId)
      });
    }

    baseTabs.push({
      label: intl.formatMessage({ id: 'social-connection.tabs.enterprise-connection-path' }),
      href: enterpriseConnectionsPath(projectId)
    });

    if (isPasskeyShown) {
      baseTabs.push({
        label: intl.formatMessage({ id: 'social-connection.tabs.passkey' }),
        href: passkeyPath(projectId)
      });
    }

    baseTabs.push({
      label: intl.formatMessage({ id: 'social-connection.tabs.miscellaneous' }),
      href: miscellaneousPath(projectId)
    });

    return baseTabs;
  }, [currentProject]);

  const shadowLoginTabItems = [
    {
      label: intl.formatMessage({ id: 'configuration-page.menu.item.callback-url' }),
      href: callbackUrlsSettingsPath(projectId)
    },
    {
      label: intl.formatMessage({ id: 'settings.tabs.platform-settings' }),
      href: platformSettingsPath(projectId)
    }
  ];

  const breadCrumbsOptions = [
    {
      label: intl.messages['dashboard.label'],
      to: dashboardPath()
    },
    {
      label: currentProject?.name,
      to: navigationPath(projectId),
      dataTestId: 'project-name'
    },
    {
      label: intl.messages['navigation.settings.authentication.label'],
      to: `#`
    }
  ];

  return (
    <NavigationWrapper
      breadCrumbsOptions={breadCrumbsOptions}
      tabsItems={currentProject?.type !== SHADOW_PROJECT_TYPE ? tabItems : shadowLoginTabItems}
      dataTestId="auth-settings">
      {children}
    </NavigationWrapper>
  );
};

export default withRouter(AuthenticationTabs);
